import { useMemo } from 'react';

import { useInViewport } from '@cast/shared-layout-ui';
import { useClusterOverviewQuery } from '@cast/shared-cost-data-access';
import { useCluster } from '@cast/shared-cluster-context-data-access';
import {
  attachChartTimestampUnix,
  createOomKillFiller,
  fillTimeSeriesGaps,
  mapClusterOverviewOomKillsTimeseries,
  mergeOomKillDataPoints,
} from '@cast/features-cluster-shared-utils';

import { useClusterDashboardOverviewQueryArgs } from './useClusterDashboardOverviewQueryArgs';

export const useClusterDashboardOomKillsData = () => {
  const inView = useInViewport();
  const { cluster } = useCluster();
  const { startTime, endTime, stepSeconds, range } =
    useClusterDashboardOverviewQueryArgs();

  const {
    data: overview,
    isLoading,
    isError,
    refetch,
  } = useClusterOverviewQuery({
    clusterId: cluster.id,
    startTime,
    endTime,
    stepSeconds,
    enabled: inView,
  });

  const { chartItems, totals } = useMemo(
    () => mapClusterOverviewOomKillsTimeseries(overview?.oomKillsTimeseries),
    [overview?.oomKillsTimeseries]
  );

  const normalizedItems = useMemo(
    () => mergeOomKillDataPoints(chartItems),
    [chartItems]
  );

  const chartData = useMemo(() => {
    if (normalizedItems.length === 0) {
      return [];
    }

    const filled = fillTimeSeriesGaps({
      timeSeries: normalizedItems,
      dateRange: range,
      filler: createOomKillFiller(),
      unit: 'second',
      step: stepSeconds,
    });

    return attachChartTimestampUnix(filled);
  }, [normalizedItems, range, stepSeconds]);

  return {
    chartData,
    totals,
    isLoading,
    isError,
    refetch,
  };
};
